import { Form } from "@inertiajs/react"
import { CheckCircle2, MapPin } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { creatorCollabShippingAddressPath } from "@/routes"
import type { CollabAddress } from "@/types/collab"

/**
 * OKS-482 — Address block on the confirm-address / preparing states.
 *
 * Read-only summary (pin + "Ship to" + recipient + single-line address). On
 * the confirm-address state an `Edit` toggle reveals an inline Inertia `<Form>`
 * (plain shadcn Input/Label + `name` attrs — NOT shadcn Form/RHF/zod) that
 * PATCHes the per-collab snapshot. Server validation errors render from the
 * render-prop `errors` object.
 *
 * OKS-999 — a successful save ALSO updates the creator's profile default
 * (server-side, in the same transaction). The form is seeded from the profile,
 * so correcting it is the act of correcting the default; the flash says so
 * (`collabs.shipping_address.updated`), which is what keeps the write
 * disclosed rather than silent. Nothing here changes to make that happen — do
 * not add a checkbox or a second request.
 *
 * OKS-994 — `editable` is true on confirm-address AND preparing: the address
 * is correctable until the parcel SHIPS, not until the creator confirms. The
 * shipped state renders no address block at all (the tracking block replaces
 * it), so there is no locked variant here.
 */
interface AddressBlockProps {
  collabId: number
  address: CollabAddress | null | undefined
  editable: boolean
  /**
   * OKS-991 — the creator's ACCOUNT phone, used only to prefill the field when
   * this parcel has none. Never submitted on its own: if they confirm without
   * touching it, the value they saw is the value that is stored, which keeps
   * the operator's provenance label honest.
   */
  accountPhone?: string | null
  /**
   * OKS-994 — may this surface OPEN ITSELF in form mode to chase a missing
   * phone? True only on confirm-address.
   *
   * ⚠️ SEPARATE FROM `editable`, and that separation is the whole point. The
   * two were the same flag until OKS-994 widened `editable` to the preparing
   * state; left coupled, every already-confirmed creator whose snapshot has no
   * phone — i.e. every row confirmed before OKS-991 — would land on the
   * mission page with the form forced open at them, chasing a value they were
   * never asked for and do not need to supply. The open-in-form branch answers
   * "something is OUTSTANDING before you can confirm"; `editable` now answers
   * "you may correct this". Different questions (UX Spec UXQ-5).
   *
   * ⚠️ REQUIRED, deliberately — no `?` and no default. Both defaults are wrong
   * in opposite directions and neither announces itself: `false` fails CLOSED
   * (the phone chase silently dies wherever a caller forgets it — it did
   * exactly that in five unit tests while `tsc` stayed green), and `true`
   * fails OPEN (the form forces itself at confirmed creators). Requiring it
   * makes every mount state its answer, and turns a forgotten wire into a
   * compile error instead of a dead branch.
   */
  promptMissingPhone: boolean
  /**
   * OKS-1036 — the address was confirmed automatically from the creator's
   * profile and they have NOT edited it since. Callers pass
   * `address_confirmation_source === "profile_auto" && !address_edited_since_confirm`
   * — both server-derived; this component never compares timestamps.
   *
   * When true, the provenance line REPLACES the `address_correctable` hint
   * (PO ruling, UX Spec §1): it already says « modifiable jusqu'à
   * l'expédition », and two lines saying it read as a glitch. After an edit the
   * caller passes false, the line goes and the hint returns, so the row looks
   * exactly like a creator-confirmed one — « confirmée automatiquement » over an
   * address they just typed would be false.
   *
   * ⚠️ REQUIRED, same reasoning as `promptMissingPhone`: `false` as a default
   * would silently kill the line wherever a mount forgets the wire.
   */
  autoConfirmed: boolean
  /**
   * OKS-1037 — the BRAND that confirmed or entered this address on the
   * creator's behalf, or null. Callers pass
   * `address_confirmation_source === "brand" ? address_confirmed_by_brand_name : null`
   * — server-derived; the name is the brand's, never the person's (PRD OQ4).
   *
   * Same slot and same `editable` gate as the auto line: it REPLACES the
   * `address_correctable` hint and says « modifiable jusqu'à l'expédition ».
   * After the creator edits, the source flips to `creator`, callers pass null,
   * and the ordinary hint returns (PRD OQ1).
   *
   * ⚠️ REQUIRED (`string | null`), same reasoning as `autoConfirmed`: an
   * optional prop would silently drop the line wherever a mount forgets it.
   */
  confirmedByBrandName: string | null
}

export function AddressBlock({
  collabId,
  address,
  editable,
  accountPhone,
  promptMissingPhone,
  autoConfirmed,
  confirmedByBrandName,
}: AddressBlockProps) {
  const { t } = useTranslation()

  // OKS-991 (UX Spec §2, PO ruling) — OPEN IN FORM MODE when this parcel has
  // no captured phone.
  //
  // ⚠️ THE REFUSAL IS A FLOOR, NOT THE DISCOVERY PATH. `Collabs::ConfirmFlag`
  // refuses `:phone_required` without a captured phone, and left to the
  // collapsed default the creator would meet that refusal as their FIRST
  // signal that anything changed: tap Confirm → error → open the form → Save →
  // Confirm. Three taps, the first of which is an error message doing the work
  // of telling them a requirement moved. The server guard stays exactly as
  // built, but it is there for a replayed or raced POST — not for ordinary
  // use.
  //
  // ⚠️ CONDITIONAL, not a removal of the toggle. A collab that already carries
  // a captured phone keeps the collapsed summary + « Modifier », because there
  // is nothing outstanding to ask for.
  //
  // ⚠️ Self-check for anyone changing this: if a creator with a profile
  // address can still reach `:phone_required` by ordinary tapping, this branch
  // is wrong — not the spec.
  //
  // ⚠️ REQUIRES AN ADDRESS TO PREFILL. A creator with NO address anywhere is a
  // different state with a different refusal — `:address_required`, not
  // `:phone_required` (PO kept the two distinct precisely so we never tell
  // someone to add a phone when what they lack is an address). Opening a
  // blank form at them replaces a clear empty state with an empty form and
  // asks them to type everything, which is the opposite of the one-tap
  // journey this branch exists to create. They get the empty state and the
  // « Modifier » toggle; the phone stays required once they open it by hand.
  //
  // ⚠️ OKS-994 — gated on `promptMissingPhone`, NOT on `editable`. See the prop
  // docstring: coupling them would force the form open on every confirmed
  // pre-OKS-991 row the moment correction was allowed.
  const needsPhone =
    promptMissingPhone && editable && !!address && !address.phone
  const [editing, setEditing] = useState(needsPhone)

  if (editing) {
    return (
      <AddressEditForm
        collabId={collabId}
        address={address}
        accountPhone={accountPhone}
        // ⚠️ NO CANCEL while a phone is outstanding. With the form open by
        // default there is no saved state to cancel BACK to — cancelling would
        // drop the creator into the collapsed summary, where the only action
        // left is Confirm, which refuses. That is the error-first path this
        // whole branch exists to close, reachable by ordinary tapping.
        cancellable={!needsPhone}
        onCancel={() => setEditing(false)}
        onSuccess={() => setEditing(false)}
      />
    )
  }

  return (
    <div
      className="bg-muted/40 flex flex-col gap-2 rounded-xl p-3"
      data-testid="gifting-address-block"
    >
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <MapPin className="size-3.5" aria-hidden="true" />
        {t("collabs.shipping_address.ship_to")}
      </div>

      {address ? (
        <div className="text-sm">
          <p className="font-semibold" data-testid="gifting-address-recipient">
            {address.recipient_name}
          </p>
          <p className="text-muted-foreground">{addressLine(address)}</p>
        </div>
      ) : (
        <p
          className="text-muted-foreground text-sm"
          data-testid="gifting-address-empty"
        >
          {t("creator_mission_detail.gifting.address_empty")}
        </p>
      )}

      {/* OKS-994 UX Spec §S1 — a one-line hint under the address saying the
          correction window is still open. Rendered only alongside a real
          address: on the empty state the creator has nothing to correct, and
          the empty-state copy already tells them to add one. */}
      {/* OKS-1036 — auto-confirmed: the provenance line takes the hint's slot
          (one line, not two). Static text, no live region — the one-time
          announcement is the flash toast on the grant redirect (UX Spec §5).
          `items-start` keeps the icon on the first line when it wraps at 375px.
          ⚠️ Gated on `editable` exactly like the hint it replaces: its copy says
          « modifiable jusqu'à l'expédition », which is false on a locked block. */}
      {/* OKS-1037 — confirmed by the brand: its line takes the same slot. The
          two provenance lines are mutually exclusive by source. */}
      {editable && confirmedByBrandName && address ? (
        <p
          className="text-muted-foreground flex items-start gap-1.5 text-xs"
          data-testid="gifting-address-source-brand"
        >
          <CheckCircle2
            className="mt-0.5 size-3.5 shrink-0"
            aria-hidden="true"
          />
          {t("collabs.shipping_address.source_brand", {
            brand: confirmedByBrandName,
          })}
        </p>
      ) : editable && autoConfirmed && address ? (
        <p
          className="text-muted-foreground flex items-start gap-1.5 text-xs"
          data-testid="gifting-address-source-auto"
        >
          <CheckCircle2
            className="mt-0.5 size-3.5 shrink-0"
            aria-hidden="true"
          />
          {t("collabs.shipping_address.source_profile_auto")}
        </p>
      ) : (
        editable &&
        address && (
          <p
            className="text-muted-foreground text-xs"
            data-testid="gifting-address-correctable"
          >
            {t("creator_mission_detail.gifting.address_correctable")}
          </p>
        )
      )}

      {editable && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          // OKS-1036 UX Spec §4 — 44px touch target below md. `sm` is not
          // floored in the primitive (OKS-874 deferred it), so the floor is
          // applied here with the in-repo idiom rather than editing `ui/`.
          className="self-start max-md:min-h-11"
          onClick={() => setEditing(true)}
          data-testid="gifting-address-edit"
        >
          {t("creator_mission_detail.gifting.edit_address")}
        </Button>
      )}
    </div>
  )
}

/** Inline snapshot editor — Inertia `<Form>`, plain inputs, render-prop errors. */
function AddressEditForm({
  collabId,
  address,
  accountPhone,
  cancellable = true,
  onCancel,
  onSuccess,
}: {
  collabId: number
  address: CollabAddress | null | undefined
  accountPhone?: string | null
  /** False when the form is the default view and there is nothing to return to. */
  cancellable?: boolean
  onCancel: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation()

  // ⚠️ `phone` sits AFTER `country` (UX Spec §2) — the address reads as a
  // label top to bottom, and the contact detail belongs after the destination
  // rather than interrupting it.
  const fields: { name: keyof CollabAddress; required: boolean }[] = [
    { name: "recipient_name", required: true },
    { name: "street", required: true },
    { name: "street_line_2", required: false },
    { name: "postal_code", required: true },
    { name: "city", required: true },
    { name: "country", required: true },
    { name: "phone", required: true },
  ]

  return (
    <Form
      action={creatorCollabShippingAddressPath(collabId)}
      method="patch"
      options={{ preserveScroll: true }}
      onSuccess={onSuccess}
      className="bg-muted/40 flex flex-col gap-3 rounded-xl p-3"
      data-testid="gifting-address-form"
    >
      {({ errors, processing }) => (
        <>
          <p className="text-sm font-semibold">
            {t("collabs.shipping_address.edit_title")}
          </p>

          {fields.map(({ name, required }) => {
            const fieldId = `collab-address-${name}`
            const errorKey = `collab_shipping_address.${name}`
            const error = errors[errorKey] ?? errors[name]
            return (
              <div key={name} className="flex flex-col gap-1">
                <Label htmlFor={fieldId} className="text-xs">
                  {t(`collabs.shipping_address.${name}`)}
                </Label>
                <Input
                  id={fieldId}
                  name={`collab_shipping_address[${name}]`}
                  // OKS-991 — the phone prefills from the creator's ACCOUNT
                  // number when this parcel has none, so the common case is
                  // one tap. It is still submitted as this parcel's own
                  // value: what they confirm here is what the operator ships
                  // against, and the server labels the provenance from where
                  // it was stored, never from where it was typed.
                  defaultValue={
                    address?.[name] ??
                    (name === "phone" ? (accountPhone ?? "") : "")
                  }
                  type={name === "phone" ? "tel" : undefined}
                  autoComplete={name === "phone" ? "tel" : undefined}
                  required={required}
                  aria-required={required || undefined}
                  aria-describedby={
                    name === "phone"
                      ? error
                        ? `${fieldId}-hint ${fieldId}-error`
                        : `${fieldId}-hint`
                      : undefined
                  }
                  maxLength={
                    name === "country" ? 2 : name === "phone" ? 32 : 200
                  }
                  aria-invalid={error ? "true" : undefined}
                  data-testid={`gifting-address-input-${name}`}
                />
                {/* The hint explains WHY a courier needs it — a required
                    field with no reason reads as data collection. */}
                {name === "phone" && (
                  <p
                    id={`${fieldId}-hint`}
                    className="text-muted-foreground text-xs"
                    data-testid="gifting-address-phone-hint"
                  >
                    {t("collabs.shipping_address.phone_hint")}
                  </p>
                )}
                {error && (
                  <p
                    role="alert"
                    id={`${fieldId}-error`}
                    className="text-destructive text-xs"
                    data-testid={`gifting-address-error-${name}`}
                  >
                    {error}
                  </p>
                )}
              </div>
            )
          })}

          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={processing}
              data-testid="gifting-address-save"
            >
              {t("collabs.shipping_address.save")}
            </Button>
            {cancellable && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={processing}
                data-testid="gifting-address-cancel"
              >
                {t("collabs.shipping_address.cancel")}
              </Button>
            )}
          </div>
        </>
      )}
    </Form>
  )
}

/** Single-line `street · postal city · country`, skipping blank segments. */
function addressLine(address: CollabAddress): string {
  const street = [address.street, address.street_line_2]
    .filter(Boolean)
    .join(", ")
  return [
    street,
    `${address.postal_code} ${address.city}`.trim(),
    address.country,
  ]
    .filter(Boolean)
    .join(" · ")
}
